import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { numberFormatter } from "@common/helpers/number";
import { currencyFormatter } from "@common/helpers/currency";
import {
    cleanExpression,
    getResultOfExpression,
} from "@components/spreadsheet/cells/utils";
import classNames from "classnames";
import commonStyles from "../SpreadsheetGrid.module.scss";
import styles from "./MathCell.module.scss";
import { BaseCellProps } from ".";

interface NumberCellProps extends BaseCellProps<number> {
    isMonetary?: boolean;
    formatDisplay?: (value: number) => React.ReactNode;
    inputClassName?: string;
    wrapperClassName?: string;
}

export const MathCell: React.FC<NumberCellProps> = ({
    value,
    isEditing,
    finishEditing,
    isMonetary,
    enterEditModeKey,
    formatDisplay,
    inputClassName,
    wrapperClassName,
}) => {
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const editSessionRef = useRef(0);
    const isFinishingRef = useRef(false);
    const [expression, setExpression] = useState<string>("");

    useLayoutEffect(() => {
        let rafId: number | undefined;

        if (isEditing && inputContainerRef.current) {
            editSessionRef.current += 1;
            isFinishingRef.current = false;
            const input = inputContainerRef.current.querySelector("input");
            if (input) {
                const hasInitialTypeKey = enterEditModeKey?.length === 1;
                let initialExpression = "";
                if (hasInitialTypeKey) {
                    initialExpression = enterEditModeKey;
                } else if (value !== null && value !== undefined) {
                    initialExpression = value.toFixed(2);
                }

                setExpression(initialExpression);

                rafId = requestAnimationFrame(() => {
                    input.focus();
                    if (!hasInitialTypeKey && input.value.length > 0) {
                        input.setSelectionRange(0, input.value.length);
                    }
                });
            }
        }

        return () => {
            if (rafId !== undefined) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [isEditing, value, enterEditModeKey]);

    const handleExpressionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setExpression(e.target.value);
    };

    const handleFinishEditing = useCallback(async () => {
        if (isFinishingRef.current) {
            return;
        }
        isFinishingRef.current = true;
        const sessionId = editSessionRef.current;
        const cleanedExpression = cleanExpression(expression);
        try {
            if (cleanedExpression === "") {
                finishEditing(null);
            } else {
                const result = await getResultOfExpression(cleanedExpression);
                if (editSessionRef.current !== sessionId) {
                    return;
                }
                finishEditing(result);
            }
        } catch {
            if (editSessionRef.current !== sessionId) {
                return;
            }
            finishEditing();
        }
    }, [expression, finishEditing]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                handleFinishEditing();
            } else if (e.key === "Escape") {
                finishEditing();
            }
        },
        [handleFinishEditing, finishEditing],
    );

    if (isEditing) {
        return (
            <div
                ref={inputContainerRef}
                className={wrapperClassName}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <input
                    value={expression}
                    onChange={handleExpressionChange}
                    onBlur={handleFinishEditing}
                    onKeyDown={handleKeyDown}
                    className={classNames(
                        inputClassName ?? styles.mathInput,
                        commonStyles.input,
                    )}
                />
            </div>
        );
    }

    if (value === null || value === undefined) {
        return null;
    }

    if (formatDisplay) {
        return formatDisplay(value);
    }

    if (isMonetary) {
        return currencyFormatter.format(value);
    }

    return numberFormatter.format(value);
};
